import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, Input, Modal, EmptyState } from '../../ui';
import { ReactComponent as LayersIcon } from '../../assets/icons/layers.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import './AdminLayout.css';

function buildTree(departments) {
  const byParent = new Map();
  departments.forEach((d) => {
    const key = d.parentId || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(d);
  });
  const walk = (parentId, depth) => {
    const children = byParent.get(parentId) || [];
    return children.flatMap((d) => [{ ...d, depth }, ...walk(d.id, depth + 1)]);
  };
  return walk(null, 0);
}

function Departments() {
  const { t } = useTranslation();
  const { has, orgId } = useOutletContext();
  const canManage = has('departments.manage');

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear, objeto = editar
  const [formName, setFormName] = useState('');
  const [formParentId, setFormParentId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/org/${orgId}/departments`)
      .then((res) => setDepartments(res.data.departments || []))
      .catch((err) => setError(err.response?.data?.error || t('errors.generic')))
      .finally(() => setLoading(false));
  }, [orgId, t]);

  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(departments), [departments]);

  const openCreate = () => { setEditing(null); setFormName(''); setFormParentId(''); setModalOpen(true); };
  const openEdit = (d) => { setEditing(d); setFormName(d.name); setFormParentId(d.parentId || ''); setModalOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await api.patch(`/org/${orgId}/departments/${editing.id}`, { name: formName, parentId: formParentId || null });
      } else {
        await api.post(`/org/${orgId}/departments`, { name: formName, parentId: formParentId || null });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(t('admin.departments.confirmDelete'))) return;
    try {
      await api.delete(`/org/${orgId}/departments/${d.id}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    }
  };

  if (loading) return <p>{t('common.loading')}</p>;

  return (
    <div>
      <div className="sgc-admin__section-header">
        <h2 className="sgc-admin__section-title">{t('admin.tabs.departments')}</h2>
        {canManage && (
          <PillButton icon={PlusIcon} onClick={openCreate}>{t('admin.departments.add')}</PillButton>
        )}
      </div>

      {error && <p className="sgc-admin__error">{error}</p>}

      <Card padded={false}>
        {tree.length === 0 ? (
          <EmptyState icon={LayersIcon} title={t('admin.departments.emptyTitle')} description={t('admin.departments.emptyDescription')} />
        ) : (
          tree.map((d) => (
            <div key={d.id} className="sgc-admin__dept-row" style={{ paddingLeft: `calc(var(--space-5) + ${d.depth * 24}px)` }}>
              <LayersIcon width={16} height={16} aria-hidden="true" />
              <span style={{ flex: 1 }}>{d.name}</span>
              {canManage && (
                <div className="sgc-admin__row-actions">
                  <button type="button" className="sgc-admin__icon-btn" onClick={() => openEdit(d)} title={t('common.edit')}>
                    <EditIcon width={16} height={16} aria-hidden="true" />
                  </button>
                  <button type="button" className="sgc-admin__icon-btn sgc-admin__icon-btn--danger" onClick={() => handleDelete(d)} title={t('common.delete')}>
                    <TrashIcon width={16} height={16} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('common.edit') : t('admin.departments.add')}>
        <form className="sgc-admin__form" onSubmit={handleSave}>
          <Input label={t('common.name')} value={formName} onChange={(e) => setFormName(e.target.value)} required />
          <select className="sgc-admin__select" value={formParentId} onChange={(e) => setFormParentId(e.target.value)}>
            <option value="">{t('admin.departments.noParent')}</option>
            {departments.filter((d) => !editing || d.id !== editing.id).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <div className="sgc-admin__form-actions">
            <PillButton type="submit" disabled={saving || !formName.trim()}>
              {saving ? t('common.loading') : t('common.save')}
            </PillButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Departments;
